import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

export type ItemizedLineItem = {
  description: string;
  category: string;
  amount: string;
  date?: string;
};

const CATEGORIES: Array<{ value: string; label: string }> = [
  { value: "supplies", label: "Supplies" },
  { value: "maintenance", label: "Maintenance" },
  { value: "repairs", label: "Repairs" },
  { value: "cleaning", label: "Cleaning" },
  { value: "service", label: "Service" },
  { value: "reimbursement", label: "Reimbursement" },
  { value: "miscellaneous", label: "Miscellaneous" },
  { value: "other", label: "Other" },
];

type Props = {
  invoiceDate: string;
  onInvoiceDateChange: (value: string) => void;
  items: ItemizedLineItem[];
  onItemsChange: (items: ItemizedLineItem[]) => void;
};

function formatMoney(amount: number): string {
  return `$${Math.abs(amount).toFixed(2)}`;
}

export default function ItemizedLineItemsEditor({ invoiceDate, onInvoiceDateChange, items, onItemsChange }: Props) {
  const subtotal = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);

  const updateAt = (idx: number, patch: Partial<ItemizedLineItem>) => {
    onItemsChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  return (
    <>
      <div>
        <Label>Invoice Date</Label>
        <Input
          type="date"
          value={invoiceDate}
          onChange={(e) => onInvoiceDateChange(e.target.value)}
          data-testid="input-itemized-date"
        />
      </div>

      <div className="space-y-2">
        <Label>Line Items</Label>
        <div className="space-y-2">
          {items.map((item, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-start p-2 border rounded-md">
              <div className="col-span-5">
                <Input
                  placeholder="Description (e.g. Cleaning supplies bulk order)"
                  value={item.description}
                  onChange={(e) => updateAt(idx, { description: e.target.value })}
                  data-testid={`input-line-description-${idx}`}
                />
              </div>
              <div className="col-span-3">
                <Select value={item.category} onValueChange={(value) => updateAt(idx, { category: value })}>
                  <SelectTrigger data-testid={`select-line-category-${idx}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-3">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={item.amount}
                  onChange={(e) => updateAt(idx, { amount: e.target.value })}
                  data-testid={`input-line-amount-${idx}`}
                />
              </div>
              <div className="col-span-1 flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => onItemsChange(items.filter((_, i) => i !== idx))}
                  disabled={items.length === 1}
                  data-testid={`button-remove-line-${idx}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onItemsChange([...items, { description: "", category: "supplies", amount: "", date: "" }])}
          data-testid="button-add-line"
        >
          <Plus className="h-4 w-4 mr-1" /> Add line item
        </Button>
        <div className="text-right text-sm font-medium pt-1">
          Subtotal: {formatMoney(subtotal)}
        </div>
      </div>
    </>
  );
}
